import { useState } from "react";
import { Button, FormField, Modal, NumberInput, TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { useMoney } from "@/shared/ui";
import type { Invoice } from "@/lib/api/types";
import {
  BILL_DISCOUNT_PERCENT_CHIPS,
  BILL_DISCOUNT_REASON_CODES,
  billDiscountPayload,
  billDiscountPreview,
  billDiscountReasonLabel,
  noteMissing,
  type ApplyBillDiscountPayload,
  type BillDiscountDraft,
} from "../lib/billDiscount";
import { VOID_REASON_NOTE_MAX_LENGTH } from "../lib/voidReasons";

/**
 * D146 — THE DISCOUNT AT THE BILL («الخصم»), the cashier's sheet.
 *
 * The manager says it across the room — «خليها 230», «10% للزبون الدايم» — so
 * the sheet takes it in the three shapes he says it in: a percent chip, an
 * amount off, or the new total («خليها»), which the SERVER back-solves through
 * the bill's own service and VAT. Then one reason chip from the closed list
 * (a note only for «أخرى»), and the new total is previewed BEFORE confirm.
 *
 * Nothing here decides money. The preview mirrors the server's arithmetic so
 * the cashier reads the figure before tapping; the server resolves the
 * discount again and refuses anything this preview missed.
 */
export function BillDiscountSheet({
  invoice,
  capPct,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  invoice: Invoice;
  /** The store's cap (`billDiscountMaxPct`), % of the bill's net. */
  capPct: number;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (payload: ApplyBillDiscountPayload) => void | Promise<void>;
}) {
  const t = useTranslate();
  const money = useMoney();
  const [draft, setDraft] = useState<BillDiscountDraft>({
    kind: "PERCENT",
    raw: "",
    reasonCode: null,
    note: "",
  });
  const preview = billDiscountPreview(invoice, draft, capPct);
  const payload = billDiscountPayload(draft);
  const hasFigure = draft.raw.trim() !== "";
  const problem = hasFigure ? preview.problem : null;
  /* The server's two bill-level refusals, said before anything is typed: one
     gesture per bill (a tier is already one), and never a platform's bill. */
  const blocked = invoice.priceTierId
    ? t((m) => m.billDiscount.blockedPriceTier)
    : invoice.aggregatorId
      ? t((m) => m.billDiscount.blockedAggregator)
      : null;
  const canConfirm =
    blocked === null && payload !== null && preview.problem === null;

  const problemText =
    problem === "OVER_CAP"
      ? t((m) => m.billDiscount.problemOverCap, {
          percent: String(capPct),
          amount: money(preview.maxAmount),
        })
      : problem === "WHOLE_BILL"
        ? t((m) => m.billDiscount.problemWholeBill)
        : problem === "BELOW_PAID"
          ? t((m) => m.billDiscount.problemBelowPaid, {
              paid: money(invoice.tenderedTotal ?? 0),
            })
          : problem === "NOTHING_OFF"
            ? t((m) => m.billDiscount.problemNothingOff)
            : null;

  const chipClass = (selected: boolean) =>
    [
      "min-h-[40px] rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-3 text-[length:var(--font-size-sm)] font-extrabold tabular-nums transition-colors",
      selected
        ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
        : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
    ].join(" ");

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={t((m) => m.billDiscount.sheetTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="bill-discount-sheet"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={isSubmitting}
            onClick={onCancel}
            data-testid="bill-discount-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="touch"
            className="font-extrabold"
            disabled={isSubmitting || !canConfirm}
            isLoading={isSubmitting}
            data-testid="bill-discount-confirm"
            onClick={() => {
              if (payload) void onConfirm(payload);
            }}
          >
            {t((m) => m.billDiscount.confirm)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {blocked ? (
          <p
            role="alert"
            className="rounded-[var(--radius-list)] border border-[color:var(--warning)] px-3 py-2 text-[length:var(--font-size-xs)] font-bold text-[color:var(--warning)]"
            data-testid="bill-discount-blocked"
          >
            {blocked}
          </p>
        ) : null}
        {/* THE PERCENT — three chips, one tap: what the manager says most. */}
        <div>
          <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
            {t((m) => m.billDiscount.percentLabel)}
          </span>
          <div
            className="flex flex-wrap gap-1.5"
            role="group"
            aria-label={t((m) => m.billDiscount.percentLabel)}
          >
            {BILL_DISCOUNT_PERCENT_CHIPS.map((percent) => {
              const selected =
                draft.kind === "PERCENT" && draft.raw === String(percent);
              return (
                <button
                  key={percent}
                  type="button"
                  aria-pressed={selected}
                  disabled={isSubmitting}
                  data-testid={`bill-discount-percent-${percent}`}
                  onClick={() =>
                    setDraft((d) => ({ ...d, kind: "PERCENT", raw: String(percent) }))
                  }
                  className={chipClass(selected)}
                >
                  <bdi dir="ltr">{percent}%</bdi>
                </button>
              );
            })}
          </div>
        </div>

        {/* AN AMOUNT OFF, and «خليها» — the new total — side by side: typing
            in one clears the other, so one kind is ever on the wire. */}
        <div className="grid grid-cols-2 gap-2">
          <FormField
            name="billDiscountAmount"
            label={t((m) => m.billDiscount.amountLabel)}
          >
            <NumberInput
              name="billDiscountAmount"
              value={draft.kind === "AMOUNT" ? draft.raw : ""}
              min={0}
              step={0.01}
              allowDecimal
              dir="ltr"
              disabled={isSubmitting}
              onChange={(event) =>
                setDraft((d) => ({ ...d, kind: "AMOUNT", raw: event.target.value }))
              }
              data-testid="bill-discount-amount"
            />
          </FormField>
          <FormField
            name="billDiscountTarget"
            label={t((m) => m.billDiscount.targetLabel)}
          >
            <NumberInput
              name="billDiscountTarget"
              value={draft.kind === "TARGET_TOTAL" ? draft.raw : ""}
              min={0}
              step={0.01}
              allowDecimal
              dir="ltr"
              placeholder={String(preview.before)}
              disabled={isSubmitting}
              onChange={(event) =>
                setDraft((d) => ({
                  ...d,
                  kind: "TARGET_TOTAL",
                  raw: event.target.value,
                }))
              }
              data-testid="bill-discount-target"
            />
          </FormField>
        </div>

        {/* THE PREVIEW — before → after, and the net taken, before confirm. */}
        <div
          className="flex flex-col gap-0.5 rounded-[var(--radius-list)] border border-[color:var(--border)] px-3 py-2 text-[length:var(--font-size-xs)] font-bold tabular-nums"
          data-testid="bill-discount-preview"
          aria-live="polite"
        >
          <div className="flex justify-between text-[color:var(--text-muted)]">
            <span>{t((m) => m.billDiscount.previewBefore)}</span>
            <bdi dir="ltr">{money(preview.before)}</bdi>
          </div>
          {hasFigure && preview.net > 0 ? (
            <>
              <div className="flex justify-between">
                <span>{t((m) => m.billDiscount.previewNet)}</span>
                <bdi dir="ltr" data-testid="bill-discount-preview-net">
                  −{money(preview.net)}
                </bdi>
              </div>
              <div className="flex justify-between text-[length:var(--font-size-md)] font-extrabold">
                <span>{t((m) => m.billDiscount.previewAfter)}</span>
                <bdi dir="ltr" data-testid="bill-discount-preview-total">
                  {money(preview.totalAfter)}
                </bdi>
              </div>
            </>
          ) : null}
          <p className="mt-0.5 text-[length:var(--font-size-2xs)] text-[color:var(--text-muted)]">
            {t((m) => m.billDiscount.capHint, {
              percent: String(capPct),
              amount: money(preview.maxAmount),
            })}
          </p>
        </div>
        {problemText ? (
          <p
            role="alert"
            className="text-[length:var(--font-size-xs)] font-bold text-[color:var(--danger)]"
            data-testid="bill-discount-problem"
          >
            {problemText}
          </p>
        ) : null}

        {/* THE REASON — the closed list, one tap; «أخرى» asks for a note. */}
        <div>
          <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
            {t((m) => m.billDiscount.reasonLabel)}
          </span>
          <div
            className="flex flex-wrap gap-1.5"
            role="group"
            aria-label={t((m) => m.billDiscount.reasonLabel)}
          >
            {BILL_DISCOUNT_REASON_CODES.map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={draft.reasonCode === code}
                disabled={isSubmitting}
                data-testid={`bill-discount-reason-${code}`}
                onClick={() => setDraft((d) => ({ ...d, reasonCode: code }))}
                className={chipClass(draft.reasonCode === code)}
              >
                {billDiscountReasonLabel(t, code)}
              </button>
            ))}
          </div>
        </div>
        {draft.reasonCode === "OTHER" ? (
          <FormField
            name="billDiscountNote"
            label={t((m) => m.billDiscount.noteLabel)}
            error={
              noteMissing(draft) && hasFigure
                ? t((m) => m.billDiscount.noteRequired)
                : undefined
            }
          >
            <TextInput
              name="billDiscountNote"
              value={draft.note}
              onChange={(event) =>
                setDraft((d) => ({ ...d, note: event.target.value }))
              }
              maxLength={VOID_REASON_NOTE_MAX_LENGTH}
              dir="auto"
              disabled={isSubmitting}
              data-testid="bill-discount-note"
            />
          </FormField>
        ) : null}
      </div>
    </Modal>
  );
}
